/* @flow */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import loadable from '@loadable/component';
import type { TCities, TCity } from '../../types/city';
import type { TDispatch } from '../../types/core';
import history from '../../history';
import { handleScrollTo } from '../../DOMUtils';
import { addDataLayer } from '../../utils';
import { formatDeadline } from '../../utils/deadlineHelpers';
import type { TProfile } from '../../types/account';
import Link from '../Link';
import {
	BenefitsWrapper,
	ContentSliderWrapper,
	DescriptionStatsWrapper,
	DescriptionTitle,
	DiscountWrapper,
	FormWrapper,
	MainGrid,
	MapRoot,
	MapWrapper,
	MortgageWrapper,
	PropsWrapper,
	RatingsWrapper,
	RelatedWrapper,
	RelatedTitleWrapper,
	Root,
	Section,
	TitleMax2,
	TitleWithLogo,
} from './styles';
import { Container } from '../Layout/styles';
import { getApartRoomsAndNumberString, getApartRoomsAndSquareString } from '../../utils/formatting';
import Breadcrumbs from '../Breadcrumbs';
import { useModal } from '../../utils/hooks';
import {
	SkeletonComplexArticles,
	SkeletonComplexImageSlider,
	SkeletonDiscounts,
	SkeletonMap,
	SkeletonRating,
} from '../Skeleton';
import { AppContext } from '../AppContext';
import { getApartInfrastructure, getApartOnMap, setApartCurrentModal } from '../../actions/apartment';
import { Logo } from '../Icons';
import StickyBar from '../StickyBar';
import type { TMortgageFormCallbackData } from '../../types/mortgage';
import Relevance from './Components/Relevance';
import type { TRating2 } from '../Rating2.0/types';
import type { TRootState } from '../../types/rootState';
import RelatedAparts from './Components/RelatedAparts/RelatedAparts';
import CallbackPopupSwitcher from '../CallbackPopupSwitcher';
import type { TFilterBase, TFilterGlobal } from '../../types/filters';
import { CLEAR_ACTION_CALLBACK_POPUP } from '../../constants/popup';
import ApartTitle from './Components/ApartTitle';
import Props from '../ApartResalePage/Components/Props';
import { LogoImg, LogoText, LogoWrapper } from '../ApartResalePage/styles';
import type { TApartment } from '../../reducers/apartment';
import { getCityRegionTitleById } from '../../actions/city';
import SortingSimilarBtn from './Components/SortingSimilarBtn';

const Modal = loadable(() => import('../Modal'), { fallback: null, ssr: false });
const ImageSlider = loadable(() => import('../ImageSlider'), {
	fallback: <SkeletonComplexImageSlider />,
});
const InfrastructureMap = loadable(() => import('../InfrastructureMap'), {
	ssr: true,
	fallback: <SkeletonMap />,
});
const Benefits = loadable(() => import('../Rating2.0/Benefits/Benefits'), {
	ssr: false,
	fallback: <SkeletonRating />,
});
const Discounts = loadable(() => import('../Discounts'), { fallback: <SkeletonDiscounts /> });
const Articles = loadable(() => import('./Components/Articles'), { fallback: <SkeletonComplexArticles /> });
const Mortgage = loadable(() => import('../Mortgage'), { fallback: null, ssr: false });
const DescriptionCharts = loadable(() => import('../DescriptionCharts'), { fallback: null, ssr: false });

type TApartProps = {
	id: number,
	label: string,
	value: string,
	link?: string,
	dataQa?: string,
};

const Apart = () => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);

	const apartment: TApartment = useSelector((state: TRootState) => state.apartment);
	const city: TCity = useSelector((state: TRootState) => state.city);
	const cities: TCities = useSelector((state: TRootState) => state.cities);
	const profile: TProfile = useSelector((state: TRootState) => state.profile);
	const filterGlobal: TFilterGlobal = useSelector((state: TRootState) => state.filterGlobal);
	const filterBase: TFilterBase = useSelector((state: TRootState) => state.filterBase);
	const isMobile: boolean = useSelector((state: TRootState) => state.app.isMobile);

	const { apartData, similarSort, currentModal } = apartment;
	const {
		building,
		stage,
		complex,
		similar,
		roomsCnt,
		number,
		onMap,
		infrastructure,
		areaTotal,
		isActive,
		isApartHotel,
		futurePriceStats,
		articles,
	} = apartData;

	const [mortgageModalData, setMortgageModalData] = useState<TMortgageFormCallbackData>(null);
	const { toggle } = useModal();

	useEffect(() => {
		const {
			complex: { id },
		} = apartData;
		const { email } = profile;

		if (history.location.hash) {
			setTimeout(() => handleScrollTo(history.location.hash.replace('#', '')), 200);
		}

		// https://livingru.atlassian.net/browse/DEV-2020
		addDataLayer({
			event: 'crto_productpage',
			crto: {
				email: email || '',
				products: [id],
			},
		});

		if (filterGlobal.selectionMode === 'mortgage') {
			setMortgageModalData({
				defaultPrice: apartData.priceTotalMin,
				initialFeeState: filterBase.mortgageInfo.initialFee,
				loanPeriodState: filterBase.mortgageInfo.loanPeriod,
				activeConfirmType: {
					value: null,
					label: null,
				},
			});
		}
	}, []);

	useEffect(() => {
		if (!onMap) {
			getApartOnMap(apartData.id, city.cityIds)(dispatch, store.getState, context);
		}
		if (!infrastructure) {
			getApartInfrastructure(complex.id, city.cityIds)(dispatch, store.getState, context);
		}
	}, [apartData]);

	const [sortedSimilar, setSortedSimilar] = useState(similar);

	useEffect(() => {
		switch (similarSort) {
			case 'PRICE_DESC':
				setSortedSimilar([...similar].sort((a, b) => b.priceTotalMin - a.priceTotalMin));
				break;
			case 'PRICE_ASC':
				setSortedSimilar([...similar].sort((a, b) => a.priceTotalMin - b.priceTotalMin));
				break;
			case 'FLOOR_DESC':
				setSortedSimilar([...similar].sort((a, b) => b.floor - a.floor));
				break;
			case 'FLOOR_ASC':
				setSortedSimilar([...similar].sort((a, b) => a.floor - b.floor));
				break;
			default:
				break;
		}
	}, [similarSort]);

	const apartNamePlusId = getApartRoomsAndNumberString(roomsCnt, number);
	const apartName = getApartRoomsAndSquareString(roomsCnt, areaTotal);
	const isApart = isApartHotel ? ' в Апарт-отеле' : ' в ЖК';
	const apartTitle = `${apartName}${complex?.name !== null ? `${isApart} «${complex.name}»` : ''}`;

	const breadcrumbs = [
		{
			index: 1,
			title: city.title,
			link: '/',
		},
		{
			index: 2,
			title: complex.name,
			link: `/${city.link}/novostroyki/${complex.slug}/`,
		},
		{
			index: 3,
			title: apartNamePlusId,
		},
	];

	const getParam = (param, extra) => {
		if (param !== null) {
			return param + (extra && ` ${extra}`);
		}
		return 'нет данных';
	};

	const apartProps: TApartProps[] = [
		{ id: 0, label: 'Площадь', value: getParam(apartData.areaTotal, `м\u00b2`), dataQa: 'apart_total_area' },
		{ id: 1, label: 'Кухня', value: getParam(apartData.areaKitchen, `м\u00b2`), dataQa: 'apart_kitchen_area' },
		{
			id: 2,
			label: 'Этаж',
			value: getParam(apartData.floor, apartData.sectionStoreysCnt ? `из ${apartData.sectionStoreysCnt}` : ''),
			link: similar.length > 0 && 'related',
			dataQa: 'apart_floor',
		},
		{ id: 3, label: 'Потолки', value: getParam(apartData.ceilingHeight, `м`), dataQa: 'apart_ceiling' },
		{
			id: 4,
			label: 'Отделка',
			value: apartData.finishing,
			dataQa: 'apart_furnish',
		},
		{
			id: 5,
			label: 'Срок сдачи',
			value: stage.stageIsPut ? 'Cдан' : formatDeadline(stage.deadline),
			dataQa: 'apart_time',
		},
	];

	const DISCOUNTS_BLOCK_ELEMENT = 'discountsBlock';

	const handleScrollToDiscounts = () => {
		handleScrollTo('discountsBlock', {
			delay: 0,
			smooth: true,
			offset: 0,
		});
	};

	const handleScrollToMortgage = () => {
		handleScrollTo('mortgageBlock', {
			delay: 0,
			smooth: true,
			offset: 0,
		});
	};

	const closeModal = () => {
		toggle();
		dispatch(setApartCurrentModal(null));
		dispatch({ type: CLEAR_ACTION_CALLBACK_POPUP });
	};

	const handlerModalToggle = (modalName: string) => {
		dispatch(setApartCurrentModal(modalName));
		toggle();
	};

	const handlerModalMortgage = (mortgageData) => {
		setMortgageModalData(mortgageData);
		handlerModalToggle('mortgage');
	};

	const RATING: TRating2 = {
		benefits: {
			houseApartment: complex.benefits[1],
			area: complex.benefits[5],
			childrenInfrastructure: complex.benefits[2],
			infrastructure: complex.benefits[3],
			transport: complex.benefits[6],
			serviceSecurity: complex.benefits[4],
		},
	};

	const mapRef = useRef();

	const handleMapClick = () => {
		mapRef.current.scrollIntoView({
			behavior: 'smooth',
		});
	};

	const futurePriceDate = futurePriceStats ? futurePriceStats[0]?.date : null;
	const futurePricePrice = futurePriceStats ? futurePriceStats[0]?.price : 0;

	const priceStatsData = [
		...apartData.priceStats,
		{
			date: futurePriceDate,
			price: futurePricePrice,
			areaAveragePrice: apartData?.areaAveragePrice,
		},
	];

	const cityTitle = getCityRegionTitleById(complex.cityId, cities);

	return (
		<Root>
			<Section isGray>
				<Container>
					{!isMobile && <Breadcrumbs data={breadcrumbs} mobileTitle="Назад" />}
					<MainGrid
						$withRelevance={!!apartData?.selectionInfo}
						$withStat={apartData?.selectionInfo?.selectionStats?.length > 0 || apartData?.stories}
						$withMortgage={isActive}
						$withRelated={similar?.length > 0}
						$withDiscount={apartData?.building?.discounts.length > 0}
						$withReviews={articles}
					>
						<ApartTitle
							isNew
							apartTitle={apartTitle}
							data={complex}
							withMap
							handleMapClick={handleMapClick}
							city={city}
							cityTitle={cityTitle}
							building={building}
						/>

						<ContentSliderWrapper>
							<ImageSlider
								id={`${complex.slug}-${apartData.id}`}
								images={complex.images}
								layout={apartData.layout}
								floorPlan={apartData.floorPlan}
								videos={complex.videos}
								panoramaLink={complex.panorama}
								tourLink={apartData.widgetLink}
								windowDirectionLink={apartData?.onMap?.windowAngle.length > 0 ? 'map' : null}
							/>
						</ContentSliderWrapper>
						<StickyBar
							handlerModalToggle={handlerModalToggle}
							handleScrollToDiscounts={handleScrollToDiscounts}
							handleScrollToMortgage={handleScrollToMortgage}
							mortgageData={mortgageModalData}
							isResale={false}
						/>
						<PropsWrapper $isConnected={!isMobile}>
							<Props data={apartProps} />
						</PropsWrapper>
						{(apartData?.stories || apartData?.selectionInfo?.selectionStats?.length > 0) && (
							<DescriptionStatsWrapper>
								<TitleWithLogo>
									<TitleMax2>Аналитика</TitleMax2>
									<LogoWrapper>
										<LogoImg viewBox="0 0 315 68">{Logo}</LogoImg>
										<LogoText>Цифровой Риелтор</LogoText>
									</LogoWrapper>
								</TitleWithLogo>
								{isMobile && (
									<DescriptionTitle>
										Мы собрали все самое интересное об этой квартире, предварительно сравнив ее с
										другими, посмотрите, что получилось
									</DescriptionTitle>
								)}
								<DescriptionCharts
									selectionStories={apartData.stories}
									selectionStats={apartData.selectionInfo?.selectionStats ?? []}
									priceStats={priceStatsData}
								/>
							</DescriptionStatsWrapper>
						)}
						{isActive && (
							<MortgageWrapper id="mortgageBlock">
								<TitleMax2>Ипотека</TitleMax2>
								<Mortgage
									defaultPrice={apartData.priceTotalMin}
									apartID={apartData.id}
									handleCallbackClick={handlerModalMortgage}
									mortgageInfo={apartData.mortgageInfo}
								/>
							</MortgageWrapper>
						)}
						{articles && <Articles />}
						<RatingsWrapper>
							<TitleMax2>
								<Link
									id="linkComplex"
									to={`/${city.link}/novostroyki/${complex.slug}/`}
									style={{ textDecoration: 'none' }}
								>
									{isApartHotel ? `АПАРТ-ОТЕЛЬ` : `ЖК`} &laquo;{complex.name}&raquo;
								</Link>
							</TitleMax2>
							<BenefitsWrapper>
								<Benefits benefits={RATING.benefits} section="total" />
							</BenefitsWrapper>
						</RatingsWrapper>
						{building.latitude && building.longitude && onMap && (
							<MapWrapper id="map" ref={mapRef}>
								<TitleMax2>Инфраструктура</TitleMax2>
								<MapRoot>
									<InfrastructureMap
										mode="apart"
										center={[building.latitude, building.longitude]}
										apartData={{
											apartName,
											buildingNum: building?.number,
											isResale: false,
											windowAngle: onMap?.windowAngle,
										}}
										complexData={{
											address: complex?.addressText,
											complexName: complex?.name,
											isComplex: complex?.isComplex,
										}}
										panorama={complex?.panorama}
										infrastructure={infrastructure?.items}
									/>
								</MapRoot>
							</MapWrapper>
						)}
						{apartData?.building?.discounts.length > 0 && (
							<DiscountWrapper id={DISCOUNTS_BLOCK_ELEMENT}>
								<TitleMax2>Скидки и акции</TitleMax2>
								<Discounts data={apartData.building.discounts} inverse />
							</DiscountWrapper>
						)}
						{!!apartData?.selectionInfo && (
							<Relevance selectionInfo={apartData.selectionInfo} city={city} />
						)}

						{sortedSimilar && !!sortedSimilar.length && (
							<RelatedWrapper id="related">
								<RelatedTitleWrapper>
									<TitleMax2>Квартиры этой планировки на других этажах</TitleMax2>
									{sortedSimilar?.length > 1 && <SortingSimilarBtn />}
								</RelatedTitleWrapper>
								<RelatedAparts similar={sortedSimilar} citySlug={city.link} />
							</RelatedWrapper>
						)}
					</MainGrid>
				</Container>
			</Section>
			<Modal isShowing={currentModal} overflowEnabled={currentModal === 'innerCallback'} hide={closeModal}>
				<FormWrapper $fullWidth={currentModal === 'innerCallback' || currentModal === 'outerCallbackDeveloper'}>
					<CallbackPopupSwitcher
						modalName={currentModal}
						mortgageData={mortgageModalData}
						apartId={apartData.id}
						handleCallbackModal={(modalName) => dispatch(setApartCurrentModal(modalName))}
					/>
				</FormWrapper>
			</Modal>
		</Root>
	);
};

export default Apart;
